import { zodResolver } from '@hookform/resolvers/zod';
import { Box, FormControl, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import type { PaymentInfo } from '@weft/types';
import { PaymentInfoSchema } from '@weft/types';
import { dayjs, isNotNullOrUndefined, now } from '@weft/utils';
import VyDialog from 'apps/app/src/components/new-design/feedback/VyDialog';
import { useReceivePayment } from 'apps/app/src/hooks/use-receive-payment';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import { Controller, useForm } from 'react-hook-form';

type MarkAsPaidDialogProps = {
  open: boolean;
  invoiceId: string;
  onClose: () => void;
  customerName: string;
};

export const MarkAsPaidDialog: React.FC<MarkAsPaidDialogProps> = ({
  open,
  invoiceId,
  onClose,
  customerName,
}) => {
  const recievePaymentMutation = useReceivePayment();

  const form = useForm<PaymentInfo>({
    resolver: zodResolver(PaymentInfoSchema),
    defaultValues: {
      date: now(),
      transactionId: '',
      note: '',
    },
  });

  const handleSubmit = form.handleSubmit(async (formData) => {
    onClose();
    await runMutation(recievePaymentMutation, {
      ...formData,
      invoiceId,
    },
    {
      pending: 'Receiving payment...',
      success: 'Payment received',
      error: 'Failed to receive payment', 
    });
  });
  
  return (
    <VyDialog
      open={open}
      onClose={onClose}
      label={`Receive payment from ${customerName}`}
      ctaText='Paid'
      cancelButtonText='Cancel'
      onSubmit={handleSubmit}
      onClick={(e) => e.stopPropagation()}
    >
      <FormControl
        sx={{ padding: '10px', width: '100%', marginBottom: '10px' }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
       
          <TextField
            fullWidth
            label='Transaction ID'
            variant="filled"
            error={isNotNullOrUndefined(form.formState.errors.transactionId)}
            helperText={form.formState.errors.transactionId?.message}
            {...form.register('transactionId')}
          />
          
          <Controller
            control={form.control}
            name="date"
            render={({ field }) => (
              <DatePicker
                label="Payment date"
                value={dayjs(field.value)}
                onChange={(date) => {
                  if (isNotNullOrUndefined(date)) {
                    form.setValue('date', date.toDate());
                  }
                }}
              />
                
            )}
          />

          <TextField
            fullWidth
            label='Note'
            variant="filled"
            error={isNotNullOrUndefined(form.formState.errors.note)}
            helperText={form.formState.errors.note?.message}
            {...form.register('note')}
          />
        </Box>
      </FormControl>
    </VyDialog>
  );
};